import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createActor, type ActorRefFrom } from 'xstate';
import type { ReservationPhone } from '@/data/models/reservation.model';
import type { BundledValidationOutput } from './machines/runBundledValidation';
import { useGoogleMaps } from '@/contexts/GoogleMapsProvider';
import { usePortalContext } from '@/contexts/PortalProvider';
import { usePortalLabel } from '@/hooks/usePortalLabel';
import { useFeatureAddressValidationForCurrentPortal } from '@/hooks/features/useFeatureAddressValidation';
import {
  reservationFormMainMachine,
  type ReservationFormMainContext,
  type ReservationFormMainFeatureFlags,
} from './machines/reservationFormMain.machine';
import type { GetDirectionsFn } from './machines/tripLegEstimatedPickup.machine';

type TripArrayFieldKey =
  | 'appointmentDate'
  | 'appointmentDateTime'
  | 'pickupDate'
  | 'pickupDateTime'
  | 'pickupAddress'
  | 'dropoffAddress'
  | 'pickupPhone'
  | 'dropoffPhone'
  | 'pickupNotes'
  | 'dropoffNotes';

type ReservationFormFieldRefs = {
  tripType: NonNullable<ReservationFormMainContext['tripTypeRef']>;
  reasonForTrip: NonNullable<ReservationFormMainContext['reasonForTripRef']>;
  appointmentDate: NonNullable<
    ReservationFormMainContext['appointmentDateRef']
  >;
  appointmentDateTime: NonNullable<
    ReservationFormMainContext['appointmentDateTimeRef']
  >;
  pickupDate: NonNullable<ReservationFormMainContext['pickupDateRef']>;
  pickupDateTime: NonNullable<ReservationFormMainContext['pickupDateTimeRef']>;
  pickupAddress: NonNullable<ReservationFormMainContext['pickupAddressRef']>;
  dropoffAddress: NonNullable<ReservationFormMainContext['dropoffAddressRef']>;
  pickupPhone: NonNullable<ReservationFormMainContext['pickupPhoneRef']>;
  dropoffPhone: NonNullable<ReservationFormMainContext['dropoffPhoneRef']>;
  pickupNotes: NonNullable<ReservationFormMainContext['pickupNotesRef']>;
  dropoffNotes: NonNullable<ReservationFormMainContext['dropoffNotesRef']>;
  willCall: NonNullable<ReservationFormMainContext['willCallRef']>;
  standingOrder: NonNullable<ReservationFormMainContext['standingOrderRef']>;
  levelOfCare: NonNullable<ReservationFormMainContext['levelOfCareRef']>;
  passengerMobility: NonNullable<
    ReservationFormMainContext['passengerMobilityRef']
  >;
  passengerAssistance: NonNullable<
    ReservationFormMainContext['passengerAssistanceRef']
  >;
  appointmentTimeAllowedForPortal: NonNullable<
    ReservationFormMainContext['appointmentTimeAllowedForPortalRef']
  >;
  tripLegEstimatedPickupRefs: ReservationFormMainContext['tripLegEstimatedPickupRefs'];
};

type ReservationFormMachinesContextValue = {
  mainActor: ActorRefFrom<typeof reservationFormMainMachine>;
  mainSnapshot: ReturnType<
    ActorRefFrom<typeof reservationFormMainMachine>['getSnapshot']
  >;
  fieldRefs: ReservationFormFieldRefs;
};

const ReservationFormMachinesContext =
  createContext<ReservationFormMachinesContextValue | null>(null);

function fieldRefsFromContext(
  ctx: ReservationFormMainContext,
): ReservationFormFieldRefs | null {
  if (
    !ctx.tripTypeRef ||
    !ctx.reasonForTripRef ||
    !ctx.appointmentDateRef ||
    !ctx.appointmentDateTimeRef ||
    !ctx.pickupDateRef ||
    !ctx.pickupDateTimeRef ||
    !ctx.pickupAddressRef ||
    !ctx.dropoffAddressRef ||
    !ctx.pickupPhoneRef ||
    !ctx.dropoffPhoneRef ||
    !ctx.pickupNotesRef ||
    !ctx.dropoffNotesRef ||
    !ctx.willCallRef ||
    !ctx.standingOrderRef ||
    !ctx.levelOfCareRef ||
    !ctx.passengerMobilityRef ||
    !ctx.passengerAssistanceRef ||
    !ctx.appointmentTimeAllowedForPortalRef ||
    ctx.tripLegEstimatedPickupRefs.length === 0
  ) {
    return null;
  }
  return {
    tripType: ctx.tripTypeRef,
    reasonForTrip: ctx.reasonForTripRef,
    appointmentDate: ctx.appointmentDateRef,
    appointmentDateTime: ctx.appointmentDateTimeRef,
    pickupDate: ctx.pickupDateRef,
    pickupDateTime: ctx.pickupDateTimeRef,
    pickupAddress: ctx.pickupAddressRef,
    dropoffAddress: ctx.dropoffAddressRef,
    pickupPhone: ctx.pickupPhoneRef,
    dropoffPhone: ctx.dropoffPhoneRef,
    pickupNotes: ctx.pickupNotesRef,
    dropoffNotes: ctx.dropoffNotesRef,
    willCall: ctx.willCallRef,
    standingOrder: ctx.standingOrderRef,
    levelOfCare: ctx.levelOfCareRef,
    passengerMobility: ctx.passengerMobilityRef,
    passengerAssistance: ctx.passengerAssistanceRef,
    appointmentTimeAllowedForPortal: ctx.appointmentTimeAllowedForPortalRef,
    tripLegEstimatedPickupRefs: ctx.tripLegEstimatedPickupRefs,
  };
}

type MainSnapshot = ReturnType<
  ActorRefFrom<typeof reservationFormMainMachine>['getSnapshot']
>;

function ReservationFormMachinesProvider({
  children,
  clientBC,
  passengerUUID,
  passengerPhone,
  standingOrderUUID,
  applyTripLegValidationResultsToForm,
  getPickupTimeZoneForTrip,
}: {
  children: ReactNode;
  clientBC: string;
  passengerUUID: string;
  passengerPhone?: ReservationPhone;
  standingOrderUUID?: string;
  /** Latest handler; read on each validation so React Hook Form can stay in sync. */
  applyTripLegValidationResultsToForm?: () => (
    output: BundledValidationOutput,
  ) => void;
  /** Returns the IANA pickup-location timezone for the given trip-leg index. */
  getPickupTimeZoneForTrip?: (tripIndex: number) => string | undefined;
}) {
  const [mainActor, setMainActor] = useState<ActorRefFrom<
    typeof reservationFormMainMachine
  > | null>(null);
  const [mainSnapshot, setMainSnapshot] = useState<MainSnapshot | null>(null);

  const { getDirections } = useGoogleMaps();
  const getDirectionsRef = useRef(getDirections);
  useEffect(() => {
    getDirectionsRef.current = getDirections;
  }, [getDirections]);

  const getDirectionsForMachine = useCallback<GetDirectionsFn>(
    (origin, destination) => getDirectionsRef.current(origin, destination),
    [],
  );

  const applyTripLegValidationResultsToFormRef = useRef(
    applyTripLegValidationResultsToForm,
  );
  useEffect(() => {
    applyTripLegValidationResultsToFormRef.current =
      applyTripLegValidationResultsToForm;
  }, [applyTripLegValidationResultsToForm]);

  const getPickupTimeZoneForTripRef = useRef(getPickupTimeZoneForTrip);
  useEffect(() => {
    getPickupTimeZoneForTripRef.current = getPickupTimeZoneForTrip;
  }, [getPickupTimeZoneForTrip]);

  const featureFlagsRef = useRef<ReservationFormMainFeatureFlags>({
    addressValidationEnabled: false,
    isClientPortal: false,
  });
  featureFlagsRef.current.addressValidationEnabled =
    useFeatureAddressValidationForCurrentPortal();
  featureFlagsRef.current.isClientPortal = usePortalLabel() === 'client-portal';

  useEffect(() => {
    const actor = createActor(reservationFormMainMachine, {
      input: {
        clientBC: '',
        passengerUUID: '',
        getDirections: getDirectionsForMachine,
        applyTripLegValidationResultsToForm: () =>
          applyTripLegValidationResultsToFormRef.current?.() ??
          ((_output: BundledValidationOutput) => {}),
        getPickupTimeZoneForTrip: (tripIndex) =>
          getPickupTimeZoneForTripRef.current?.(tripIndex),
        featureFlags: featureFlagsRef.current,
      },
    });
    actor.start();
    setMainActor(actor);
    setMainSnapshot(actor.getSnapshot());
    const sub = actor.subscribe(setMainSnapshot);
    return () => {
      sub.unsubscribe();
      actor.stop();
      setMainActor(null);
      setMainSnapshot(null);
    };
  }, []);

  useEffect(() => {
    if (!mainActor) return;
    mainActor.send({
      type: 'SET_PASSENGER',
      clientBC,
      passengerUUID,
      passengerPhone,
      standingOrderUUID,
    });
  }, [mainActor, clientBC, passengerUUID, passengerPhone, standingOrderUUID]);

  const { isMemberPortal, portalName } = usePortalContext();
  const effectivePortalName = isMemberPortal ? portalName : null;
  useEffect(() => {
    if (!mainActor) return;
    mainActor.send({
      type: 'SET_PORTAL_NAME',
      portalName: effectivePortalName,
    });
  }, [mainActor, effectivePortalName]);

  const fieldRefs = useMemo(() => {
    if (!mainSnapshot) return null;
    return fieldRefsFromContext(mainSnapshot.context);
  }, [mainSnapshot]);

  const value = useMemo(() => {
    if (!mainActor || !mainSnapshot || !fieldRefs) return null;
    return { mainActor, mainSnapshot, fieldRefs };
  }, [mainActor, mainSnapshot, fieldRefs]);

  if (!value) {
    return null;
  }

  return (
    <ReservationFormMachinesContext.Provider value={value}>
      {children}
    </ReservationFormMachinesContext.Provider>
  );
}

function useReservationFormMachines(): ReservationFormMachinesContextValue {
  const context = useContext(ReservationFormMachinesContext);
  if (!context) {
    throw new Error(
      'useReservationFormMachines must be used within ReservationFormMachinesProvider',
    );
  }
  return context;
}

export type {
  TripArrayFieldKey,
  ReservationFormFieldRefs,
  ReservationFormMachinesContextValue,
};

export { ReservationFormMachinesProvider, useReservationFormMachines };
